import React, { useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Checkbox,
  FormControlLabel,
  Divider,
  useTheme,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PackagingSwagpack } from "../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import AppButton from "../../../components/common/buttons/AppButton";
import { useNotification } from "../../../../utils/NotificationsContext";
import CustomTypography from "../../../components/common/form/CustomTypography";
import CustomTextField from "../../../components/common/textFields/TextField";
import { PackToDelete } from "../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import { useStores } from "../../../../use-stores";
import { observer } from "mobx-react";

interface RemovePackPopupProps {
  open: boolean;
  pack: PackagingSwagpack | null;
  onConfirm: (packToDelete: PackToDelete) => void;
  onCancel: () => void;
}

const RemovePackPopup: React.FC<RemovePackPopupProps> = ({
  open,
  pack,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const { projectPackagingOpsStore } = useStores();

  const isBulkCase =
    pack?.products?.length === 1 && pack?.products?.[0]?.variants?.length > 0;

  useEffect(() => {
    if (open && pack) {
      if (isBulkCase) {
        const variants = pack.products[0].variants || [];
        const availableQuantities: { [key: number]: number } = {};
        variants.forEach((variant) => {
          availableQuantities[variant.id] = variant.quantity || 0;
        });
        projectPackagingOpsStore.setVariantAvailableQuantities(
          availableQuantities
        );

        projectPackagingOpsStore.setVariantSelections(
          variants.map((variant) => ({
            id: variant.id,
            size: variant.size,
            quantity: 0,
          }))
        );
        projectPackagingOpsStore.clearSelectedVariantIds();
      } else {
        projectPackagingOpsStore.setRemoveQuantity(pack.quantity || 0);
      }
    }
  }, [open, pack, isBulkCase, projectPackagingOpsStore]);

  const handleVariantQuantityChange = (variantId: number, value: string) => {
    if (value === "" || value === null || value === undefined) {
      projectPackagingOpsStore.updateVariantSelection(variantId, "");
    } else {
      const numValue = parseInt(value) || 0;
      projectPackagingOpsStore.updateVariantSelection(variantId, numValue);
    }
  };

  const handleRegularPackQuantityChange = (value: string) => {
    if (value === "" || value === null || value === undefined) {
      projectPackagingOpsStore.setRemoveQuantity("" as any);
    } else {
      const numValue = parseInt(value) || 0;
      projectPackagingOpsStore.setRemoveQuantity(numValue);
    }
  };

  const handleConfirm = () => {
    if (!pack) return;

    if (isBulkCase) {
      const selectedVariantIds = projectPackagingOpsStore.selectedVariantIds;
      const invalidVariants =
        projectPackagingOpsStore.variantSelections?.filter(
          (vs) =>
            selectedVariantIds?.includes(vs.id) &&
            (Number(vs.quantity) <= 0 ||
              vs.quantity === null ||
              vs.quantity === undefined)
        );

      if (invalidVariants?.length > 0) {
        const invalidSizes = invalidVariants?.map((v) => v.size).join(", ");
        showError(
          "Validation Error",
          `Selected variant(s) with size(s) "${invalidSizes}" must have quantity greater than 0`
        );
        return;
      }

      const selectedVariants =
        projectPackagingOpsStore.variantSelections.filter(
          (vs) => selectedVariantIds.includes(vs.id) && vs.quantity > 0
        );

      if (selectedVariants?.length === 0) {
        showError(
          "Validation Error",
          "Please select at least one variant that has a quantity greater than zero"
        );
        return;
      }

      onConfirm({
        inventory_id: pack.inventory_id,
        swagpack_id: pack.swagpack_id,
        bulk_remove_quantity: selectedVariants,
      });
    } else {
      const removeQty = Number(projectPackagingOpsStore.removequantity) || 0;
      if (removeQty <= 0) {
        showError("Validation Error", "Remove quantity must be greater than 0");
        return;
      }

      onConfirm({
        inventory_id: pack.inventory_id,
        swagpack_id: pack.swagpack_id,
        pack_remove_quantity: removeQty,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Remove Quantity from {isBulkCase ? "Bulk" : "Pack"}
        <IconButton aria-label="close" onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <CustomTypography variant="body1">Name:</CustomTypography>
          <CustomTypography variant="heading7">
            {pack?.swagpack_name || ""}
          </CustomTypography>
        </Box>
        <Divider sx={{ my: 2 }} />
        {isBulkCase ? (
          <>
            <CustomTypography variant="body2" style={{ marginBottom: "10px" }}>
              Select the variant and specify the quantity you want to remove
            </CustomTypography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {projectPackagingOpsStore.variantSelections
                .filter((vs) => {
                  const availableQty =
                    projectPackagingOpsStore.variantAvailableQuantities[
                      vs.id
                    ] || 0;
                  return availableQty > 0;
                })
                .map((vs) => {
                  const availableQty =
                    projectPackagingOpsStore.variantAvailableQuantities[
                      vs.id
                    ] || 0;
                  const isSelected =
                    projectPackagingOpsStore.selectedVariantIds.includes(vs.id);

                  return (
                    <Box
                      key={vs.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        backgroundColor: isSelected
                          ? theme.palette.action.selected
                          : "transparent",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <FormControlLabel
                          label=""
                          control={
                            <Checkbox
                              checked={isSelected}
                              disabled={availableQty === 0}
                              onChange={() =>
                                projectPackagingOpsStore.toggleVariantSelection(
                                  vs.id
                                )
                              }
                            />
                          }
                          sx={{ flex: 1, m: 0 }}
                        />
                        <Box>
                          <CustomTypography variant="body1">
                            Size: {vs.size}
                          </CustomTypography>
                          <CustomTypography variant="body2">
                            Available Qty: {availableQty}
                          </CustomTypography>
                        </Box>
                      </Box>

                      <CustomTextField
                        allowDecimalValue={false}
                        allowNegativeValue={false}
                        borderColor={`1px solid ${theme.palette.customColors.greyDarkborder}`}
                        value={vs.quantity === 0 ? "" : vs.quantity}
                        onChange={(e: any) =>
                          handleVariantQuantityChange(vs.id, e.target.value)
                        }
                        placeholder="0"
                        type="number"
                        disabled={!isSelected}
                        width={120}
                        customStyles={{
                          opacity: isSelected ? 1 : 0.5,
                        }}
                      />
                    </Box>
                  );
                })}
            </Box>
          </>
        ) : (
          <Box>
            <CustomTypography variant="body1" style={{ marginBottom: "10px" }}>
              Available Quantity:{" "}
              {`${(pack?.quantity ?? 0) - (pack?.used_quantity ?? 0)}`}
            </CustomTypography>

            <CustomTextField
              allowDecimalValue={false}
              allowNegativeValue={false}
              borderColor={`1px solid ${theme.palette.customColors.greyDarkborder}`}
              value={""}
              placeholder="0"
              onChange={(e: any) =>
                handleRegularPackQuantityChange(e.target.value)
              }
              type="number"
              helperText="Enter the quantity to remove from this pack"
              width="100%"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton
          onClick={onCancel}
          label="Cancel"
          customStyles={{
            height: "2.3rem",
            minWidth: "5.438rem",
            backgroundColor: theme.palette.common.white,
            color: theme.palette.customColors.greyTextColor,
          }}
          variant="grayscale"
        />
        <AppButton
          label="Remove"
          onClick={handleConfirm}
          customStyles={{
            height: "2.3rem",
            minWidth: "5.438rem",
            backgroundColor: theme.palette.customColors.purple,
            color: theme.palette.customColors.white,
          }}
          variant="primary"
        />
      </DialogActions>
    </Dialog>
  );
};

export default observer(RemovePackPopup);
